import {Dispatch, SetStateAction, useCallback, useState} from 'react'

/**
 * Session-persisted list state.
 *
 * List pages lose their filters, sorting and page position whenever the user opens a
 * record and navigates back, because the page component unmounts. These helpers back a
 * piece of `useState` with `sessionStorage` so the grid is restored exactly as the user
 * left it. `sessionStorage` (rather than `localStorage`) is deliberate: state survives
 * navigation and reloads within the tab, but a new tab or a new browser session starts
 * clean.
 */

const NAMESPACE = 'jj:list'

const buildKey = (key: string) => `${NAMESPACE}:${key}`

const isFunction = <T,>(value: unknown): value is (...args: any[]) => T =>
  typeof value === 'function'

const resolveInitial = <T,>(initial: T | (() => T)): T =>
  isFunction<T>(initial) ? initial() : initial

function readStoredValue<T>(key: string, initial: T | (() => T)): T {
  try {
    const raw = sessionStorage.getItem(buildKey(key))
    if (raw === null) return resolveInitial(initial)
    return JSON.parse(raw) as T
  } catch {
    return resolveInitial(initial)
  }
}

function writeStoredValue<T>(key: string, value: T): void {
  try {
    if (value === undefined) {
      sessionStorage.removeItem(buildKey(key))
    } else {
      sessionStorage.setItem(buildKey(key), JSON.stringify(value))
    }
  } catch {
    // Storage can be unavailable (private mode, quota). State still works in memory.
  }
}

/**
 * Returns true when a value for this key has already been persisted in the current
 * session. Pages that seed a default filter on mount use this to avoid overwriting the
 * state the user is returning to.
 */
export function hasSessionState(key: string): boolean {
  try {
    return sessionStorage.getItem(buildKey(key)) !== null
  } catch {
    return false
  }
}

/**
 * Drop-in replacement for `useState` that persists the value for the browser session.
 *
 * @param key Stable identifier, namespaced per page — e.g. `'company.filters'`.
 */
export function useSessionState<T>(
  key: string,
  initial: T | (() => T)
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => readStoredValue(key, initial))

  const setPersistedValue = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (prevState: T) => T)(prev) : next
        writeStoredValue(key, resolved)
        return resolved
      })
    },
    [key]
  )

  return [value, setPersistedValue]
}

/**
 * Drops every persisted list state. Called on logout so the next admin to use the tab
 * does not inherit the previous one's filters.
 */
export function clearSessionListState(): void {
  try {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(`${NAMESPACE}:`)) keys.push(key)
    }
    keys.forEach((key) => sessionStorage.removeItem(key))
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
